import { useEffect, useMemo } from "react";
import { useObservable } from "micro-observables";
import { useSelector } from "react-redux";

import { ROUTES_MAP } from "../routes/constants";
import { PERMISSIONS } from "../constants/permissions/permissions";
import { menuMetricGroups, requestMenuMetricGroups } from "../modules/Metric/services/groups/groupsService";
import MENU_ITEMS from "../configs/menuItems";
import { getAuthPolicySelector } from "../modules/App/state/selectors";
import { modulesInitialFilters } from "../constants/modulesInitialFilters";
import { getDateFromData } from "../utils/helpers/dateTime";

const useMenu = () => {
    const authPolicyCodes = useSelector(getAuthPolicySelector);
    const groups = useObservable(menuMetricGroups);

    const isHasTrackerFilterIsSandboxMode = useMemo(
        () => authPolicyCodes.includes(PERMISSIONS.trackerFilterIsSandboxMode),
        [ authPolicyCodes ]
    );

    useEffect(
        () => {
            if(authPolicyCodes.includes("metric-groups.view")) {
                void requestMenuMetricGroups();
            }
        },
        [ authPolicyCodes ]
    );

    const metrics = useMemo(
        () => {
            const metricsMenuItems = [
                {
                    isDividerBefore: true,
                    name: "Metric Groups",
                    path: `${ROUTES_MAP.metric.path}/groups`,
                    permission: PERMISSIONS.metricGroupUpdate
                },
                {
                    name: "Metric Tasks",
                    path: `${ROUTES_MAP.metric.path}/tasks`,
                    permission: PERMISSIONS.metricTaskUpdate
                },
                {
                    name: "Collection Status",
                    path: ROUTES_MAP.metric.createCollectionStatusURL(modulesInitialFilters.collectionStatus),
                    permission: PERMISSIONS.metricDataCollectionStatus
                },
                {
                    name: "Keyword Scan Stats",
                    path: ROUTES_MAP.metric.createKeywordScanStatsURL(),
                    permission: PERMISSIONS.metricKeywordScanStats
                }
            ];
            if(groups.length === 0) {
                return ({
                    name: "Metrics",
                    path: ROUTES_MAP.metric.path,
                    permission: PERMISSIONS.metricsMenuItem,
                    subMenu: [ ...metricsMenuItems ]
                });
            }

            const restSubMenu = groups.map(({ name, code }) => ({
                name,
                path: `${ROUTES_MAP.metric.path}/list/${code}`,
                permission: PERMISSIONS.metricsMenuItem
            }));

            return ({
                name: "Metrics",
                path: ROUTES_MAP.metric.path,
                permission: PERMISSIONS.metricsMenuItem,
                subMenu: [
                    ...restSubMenu,
                    ...metricsMenuItems
                ]
            });
        },
        [ groups ]
    );

    return useMemo(
        () => {
            const menuItems = [ ...MENU_ITEMS ];

            if(!isHasTrackerFilterIsSandboxMode) {
                const trackerParams = { ...modulesInitialFilters.tracker };

                trackerParams.is_sandbox_mode = false;
                trackerParams.created_at_initial_from = `${getDateFromData(new Date(), "YYYY-MM-DD")} 00:00:00`;
                trackerParams.created_at_initial_to = `${getDateFromData(new Date(), "YYYY-MM-DD")} 23:59:59`;

                menuItems.reduce(
                    (acc, item) => {
                        if(item.name === "Tracker") {
                            acc.push({
                                ...item,
                                path: ROUTES_MAP.tracker.createURL(trackerParams)
                            });
                        } else {
                            acc.push(item);
                        }

                        return acc;
                    },
                    []
                );
            }

            if(metrics) {
                menuItems.push(metrics);
            }

            return menuItems;
        },
        [ isHasTrackerFilterIsSandboxMode, metrics ]
    );
};


export default useMenu;
